import "./FundingProgress.scss";

import { Box, Grid, Typography } from "@material-ui/core";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SaleStatus } from '../../backend/types/utils';
import { CreateSaleManageable, SaleManageable } from "../../store/sale/types";
import { SubformProps } from "../SubformProps";

interface TextPerValue {
  value: number;
  text: string;
}

export interface FundingProgressProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  className?: string;
}

type SaleStatusToTextMapping = {
  [key in SaleStatus]: string
}

export const FundingProgress = (props: FundingProgressProps) => {
  // Props
  const {
    className,
    values,
  } = props;

  const sale = new SaleManageable(values);

  const status = sale.status;
  const percentageFunded = sale.percentageFunded || 0;

  // Hooks
  const { t } = useTranslation();

  // Options
  const saleStatusToStatusTextMapping : SaleStatusToTextMapping = {
    [SaleStatus.INACTIVE]: t("INACTIVE"),
    [SaleStatus.ACTIVE]: t("ACTIVE"),
    [SaleStatus.PAUSED]: t("PAUSED"),
    [SaleStatus.FINISHED]: t("FINISHED"),
    [SaleStatus.ABORTED]: t("ABORTED"),
    [SaleStatus.FINALIZATION_PENDING]: t("FINALIZATION PENDING"),
  }

  const textsPerFundingLevel: Array<TextPerValue> = useMemo(
    () => [{
      value: 10,
      text: t("This investment product has recently become investable. Be one of the first investors on board!"),
    }, {
      value: 40,
      text: t("Various investors have already put their trust in this investment and invested. Join them!"),
    }, {
      value: 60,
      text: t("Half of the tokens have already been sold. There is still time to get in!"),
    }, {
      value: 80,
      text: t("Slowly the supply is running low. Secure your tokens before they sell out!"),
    }, {
      value: 100,
      text: t("There are only a few tokens left. Now is the time to be quick!"),
    }],
    []
  );

  const fundingLevelText = useMemo(
    () => textsPerFundingLevel.find((entry: TextPerValue) => percentageFunded <= entry.value)?.text || "",
    [textsPerFundingLevel, percentageFunded]
  );

  const saleStatusToDescriptionMapping: SaleStatusToTextMapping = {
    [SaleStatus.INACTIVE]: t("This asset will be investable shortly. Check back soon! Or get notified here!"),
    [SaleStatus.ACTIVE]: fundingLevelText,
    [SaleStatus.PAUSED]: t("The possibility to invest in this asset is currently paused."),
    [SaleStatus.FINISHED]: t("All tokens of this asset were sold! The offer is finished."),
    [SaleStatus.ABORTED]: t("This offer has been terminated early."),
    [SaleStatus.FINALIZATION_PENDING]: t("All tokens of this asset have been sold! The offer is in the process of being finalized."),
  }

  return (
    <Grid container direction="column" item className={className + " " + "funding-progress"}>
      <Grid container direction="row" item>
        <Grid item xs={6}/>

        <Grid item xs={6} className={`status-box-${status}`}>
          <Typography className="text">
            {t("Status")}: <span className="text--value">{saleStatusToStatusTextMapping[status]}</span>
          </Typography>
        </Grid>
      </Grid>

      <Grid container direction="row" item className="content-box">
        <Grid item>
          <Typography className="status-label">
            {saleStatusToDescriptionMapping[status]}
          </Typography>
        </Grid>

        <Grid item className="grid-container">
          <Typography className="percentage-value">
            {percentageFunded}%
          </Typography>

          <Box className="progress-bar">
            <Box className="frame" />
            <Box className="value" width={percentageFunded + "%"}/>
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
}